import * as SentrySDK from '@sentry/react';

import { UserInformation } from '#/models/UserInformation';
import { getEnvironment, isSentryEnabled } from '#lib/Config';

/**
 * Network-level fetch failures reported by the browser when a request never
 * completes (lost connectivity, backgrounded tab, navigation aborting an
 * in-flight request). These are noise — they are not HTTP errors (real 4xx/5xx
 * reach Sentry through handleErrors → UnexpectedError) — and vary by engine.
 *
 * Single source of truth: `filterEvent` (Sentry `beforeSend`) and the network
 * client's `isNetworkError` (src/lib/network/helpers.ts) both rely on this list.
 */
export const NETWORK_ERROR_MESSAGES = [
  'Load failed', // WebKit / Safari / iOS
  'Failed to fetch', // Chrome / Blink
  'NetworkError', // Firefox
  'Network request failed', // generic
  'The network connection was lost', // iOS
  'The request timed out', // iOS
  'cancelled', // WebKit (aborted request)
  'Could not connect to the server' // iOS
];

/**
 * Only report errors that are not BadRequest, NotFound or known network failures
 * @param event
 * @returns {boolean} true if the event should be filtered and not reported to Sentry
 */
export const filterEvent = (event: any): boolean => {
  // In case there's a chain, we take the last entries
  const mainAndMaybeCauseErrors = event.exception?.values?.slice(-2) ?? [];

  for (const error of mainAndMaybeCauseErrors) {
    const { type, value } = error;

    // Unexpected type of event (reported always on Sentry)
    if (typeof value !== 'string') {
      return false;
    }

    if (value.startsWith('[BadRequest]')) {
      return true;
    }

    const is404 = type === 'NotFoundError' && !!value.match('(GET|POST|PUT|DELETE) .* 404');
    if (is404) {
      return true;
    }

    const isNetworkError = type === 'TypeError' && NETWORK_ERROR_MESSAGES.some(message => value.includes(message));
    if (isNetworkError) {
      return true;
    }
  }

  return false;
};

export default class Sentry {
  private isEnabled: boolean;
  private isInitialized: boolean;

  constructor() {
    this.isInitialized = false;
    this.isEnabled = isSentryEnabled();
  }

  start() {
    this.initialize();
  }

  initialize() {
    if (!this.isEnabled) {
      return;
    }
    if (this.isInitialized) {
      return;
    }
    SentrySDK.init({
      dsn: 'https://802abc860eea494e925228746594324e@o551562.ingest.sentry.io/5675129',
      release: `zzm-front@${import.meta.env.REACT_APP_VERSION}`,
      integrations: [
        SentrySDK.httpContextIntegration(),
        SentrySDK.browserApiErrorsIntegration({
          setTimeout: true,
          setInterval: true,
          requestAnimationFrame: true,
          XMLHttpRequest: true,
          eventTarget: true
        }),
        SentrySDK.browserSessionIntegration(),
        SentrySDK.functionToStringIntegration(),
        SentrySDK.globalHandlersIntegration({
          onerror: true,
          onunhandledrejection: true
        }),
        SentrySDK.captureConsoleIntegration({
          levels: ['error']
        }),
        SentrySDK.httpClientIntegration()
      ],
      environment: getEnvironment(),
      tracesSampleRate: 0.3,
      ignoreErrors: ['ResizeObserver', 'Usetiful', "reading 'postMessage'"],
      beforeSend(event) {
        if (filterEvent(event)) {
          return null;
        }
        return event;
      }
    });
    this.isInitialized = true;
  }

  setUser(userInformation: UserInformation) {
    if (!this.isEnabled) {
      return;
    }
    if (!this.isInitialized) {
      return;
    }
    SentrySDK.setUser(userInformation);
  }

  clear() {
    if (!this.isEnabled) {
      return;
    }
    if (!this.isInitialized) {
      return;
    }
    SentrySDK.setUser(null);
  }

  reportError(error: Error, errorInfo) {
    if (!this.isEnabled) {
      return;
    }
    if (!this.isInitialized) {
      return;
    }
    SentrySDK.captureException(error, { extra: errorInfo });
  }
}
